import { InputLabel } from '@material-ui/core';
import FormHelperText from '@material-ui/core/FormHelperText';
import MuiInput from '@material-ui/core/Input';
import InputAdornment from '@material-ui/core/InputAdornment';
import MuiList from '@material-ui/core/List';
import MuiListItem from '@material-ui/core/ListItem';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import { IconButton } from '@monorepo/shared/componentsV2/Button/IconButton';
import { ReorderableList } from '@monorepo/shared/componentsV2/dragAndDrop/ReorderableList';
import {
  DraggableElement,
  DragHandle,
  ListElement,
} from '@monorepo/shared/componentsV2/dragAndDrop/styled';
import { VisuallyHidden } from '@monorepo/shared/componentsV2/VisuallyHidden';
import keyBy from 'lodash.keyby';
import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { bodyMedium } from '../styles/text';
import { Button } from './Button';
import { Tooltip } from './Tooltip';

const Input = styled(MuiInput)`
  position: relative;
  display: flex;
  align-items: center;
  min-width: 35rem;
  height: 2rem;
  padding: 0 0.5rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.gray333};
  border: 1px solid ${({ theme }) => theme.colors.grayddd};
  border-radius: 4px;

  & svg {
    width: 0.875rem;
    height: 0.875rem;
  }

  &.Mui-error {
    border-color: ${({ theme }) => theme.colors.darkRed};
  }
` as typeof MuiInput;

const Options = styled.fieldset`
  display: inline-flex;
  flex-direction: column;
  min-width: 0;
  padding: 0;
  margin: 0;
  vertical-align: top;
  border: 0;
`;

const StyledInputLabel = styled(InputLabel)`
  ${bodyMedium}
  color: ${({ theme }) => theme.colors.gray333};
`;

const HelperText = styled(FormHelperText)`
  &.Mui-error {
    color: ${({ theme }) => theme.colors.darkRed};
  }
`;

export const AddButton = styled(Button)`
  max-width: 7rem;
`;

const StyledReorderableList = styled(ReorderableList)`
  padding-top: 8px;
  padding-bottom: 8px;
  margin: 0;

  ${ListElement} {
    padding-top: 4px;
    padding-bottom: 4px;
  }

  & ${DraggableElement} {
    margin: 0;

    & ${DragHandle} {
      left: -1.35rem;
      padding: 0.5rem;
      transform: scale(0.75);
    }
  }
`;

interface ListItem {
  id: string;
  itemValue: string;
}

interface ReorderableListProps {
  isReorderable: true;
  uniqueDragListId: string;
  listAriaLabel: string;
}

interface ListBuilderProps {
  addItemButtonLabel?: string;
  className?: string;
  error?: boolean;
  errorText?: string;
  helperText?: string;
  isDisabled?: boolean;
  isLabelHidden?: boolean;
  isRequired?: boolean;
  label: string;
  name: string;
  onChange: (value: string[]) => void;
  reorderableProps?: ReorderableListProps;
  toolTipText?: string;
  value: string[];
}

export function ListBuilder({
  addItemButtonLabel = 'Add item',
  className,
  error,
  errorText,
  helperText,
  isDisabled = false,
  isLabelHidden = false,
  isRequired = false,
  label,
  name,
  onChange,
  reorderableProps,
  toolTipText,
  value,
}: ListBuilderProps) {
  const [idRandomify] = useState(uuidv4);
  const elementId = `${name}-${idRandomify}`;
  const [list, setList] = useState<ListItem[]>(
    (value || ['']).map((itemValue) => ({ id: uuidv4(), itemValue })),
  );

  const addItem = () => {
    setList([...list, { id: uuidv4(), itemValue: '' }]);
  };

  const filterAndSubmit = useCallback(
    (l: ListItem[]) => {
      const newList = l.map((item) => item.itemValue).filter((item) => !!item);
      onChange(newList);
    },
    [onChange],
  );

  const handleChange = useCallback(
    (id) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const newList = [...list];
      const index = newList.findIndex(({ id: itemId }) => itemId === id);
      if (index === -1) return;
      const item = newList[index];
      if (!item) return;
      item.itemValue = e.target.value;
      setList(newList);
      filterAndSubmit(newList);
    },
    [filterAndSubmit, list],
  );

  const handleOrderChange = useCallback(
    (orderedIds: string[]) => {
      const idsToItems = keyBy(list, 'id');
      const newList = orderedIds
        .map((id) => idsToItems[id])
        // satisfies the typechecker to handle unfound IDs, though they shouldn't happen
        .filter((item): item is ListItem => item !== undefined);
      setList(newList);
      filterAndSubmit(newList);
    },
    [filterAndSubmit, list],
  );

  const removeItem = useCallback(
    (idToRemove: string) => () => {
      if (list.length === 1) {
        return;
      }
      const newList = list.filter(({ id }) => id !== idToRemove);
      setList(newList);
      filterAndSubmit(newList);
    },
    [filterAndSubmit, list],
  );

  const items = useMemo(
    () =>
      list.map(({ id, itemValue }, index) => {
        const component = (
          <div key={id}>
            <VisuallyHidden>
              <label htmlFor={id}>{`${label} item ${index + 1}`}</label>
            </VisuallyHidden>
            <Input
              disabled={isDisabled}
              disableUnderline
              endAdornment={
                !isDisabled &&
                list.length > 1 && (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={`remove ${label} item ${index + 1}`}
                      onClick={removeItem(id)}
                    >
                      <HighlightOffIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }
              error={error}
              id={id}
              onChange={handleChange(id)}
              value={itemValue}
            />
          </div>
        );
        return { id, component };
      }),
    [isDisabled, label, list, removeItem, error, handleChange],
  );

  const tooltip = toolTipText ? <Tooltip title={toolTipText} /> : '';

  return (
    <Options className={className}>
      {isLabelHidden ? (
        <VisuallyHidden>{label}</VisuallyHidden>
      ) : (
        <StyledInputLabel error={error} required={isRequired}>
          {label}
          {tooltip}
        </StyledInputLabel>
      )}
      {reorderableProps?.isReorderable ? (
        <StyledReorderableList
          orderedElements={items}
          onOrderChange={handleOrderChange}
          uniqueDragListId={reorderableProps.uniqueDragListId}
          listAriaLabel={reorderableProps.listAriaLabel}
        />
      ) : (
        <MuiList
          id={elementId}
          aria-describedby={helperText ? `${elementId}-helper-text` : undefined}
        >
          {items.map(({ id, component }) => (
            <MuiListItem dense disableGutters key={id}>
              {component}
            </MuiListItem>
          ))}
        </MuiList>
      )}
      <HelperText error={error} id={`${elementId}-helper-text`}>
        {errorText || helperText}
      </HelperText>
      {!isDisabled && (
        <AddButton color="primary" onClick={addItem}>
          <AddCircleOutlineIcon />
          {addItemButtonLabel}
        </AddButton>
      )}
    </Options>
  );
}
